import React, { createContext, useContext } from "react";
import type { DocumentElement } from "@eaco/forms-core";

/**
 * A renderer for a single `DocumentElement` — host apps provide one
 * component per supported typeId. The dispatcher (`DocumentElement`
 * in this package) reads the registry from context and looks up the
 * component matching `element.typeId`.
 *
 * Splitting "which UI does this typeId render?" from the engine
 * lets the same FillFormBody / DocumentView orchestrate either:
 *   - apps/web's full 54-renderer Radix/shadcn set
 *   - apps/forms's anonymous-fill subset (entity pickers stubbed)
 *   - (future) any other registry, e.g. read-only print rendering
 */
export type ElementRenderer = React.ComponentType<{
  element: DocumentElement;
}>;

export type ElementRegistry = Record<string, ElementRenderer | undefined>;

const ElementRegistryContext = createContext<ElementRegistry>({});

interface ProviderProps {
  registry: ElementRegistry;
  children: React.ReactNode;
}

export const ElementRegistryProvider: React.FC<ProviderProps> = ({
  registry,
  children,
}) => (
  <ElementRegistryContext.Provider value={registry}>
    {children}
  </ElementRegistryContext.Provider>
);

/** Returns the active registry. Empty when no provider is mounted. */
export const useElementRegistry = (): ElementRegistry =>
  useContext(ElementRegistryContext);
